import type { BlogPost } from '../../types/blog';

export const post: BlogPost = {
  category: 'guias',
  author: 'Ivan Henrique',
  publishedAt: '2026-04-30',
  coverImage: '/blog/libertadores-2026.svg',
  tags: ['Libertadores', 'CONMEBOL', 'Sul-Americana', 'Bolão'],
  i18n: {
    'pt-BR': {
      slug: 'libertadores-2026',
      title: 'Libertadores 2026: fase de grupos, oitavas e o segredo dos confrontos cruzados',
      excerpt:
        'O torneio mais importante da América do Sul tem 32 times na fase de grupos, mata-mata em 8 etapas e final em jogo único. Entenda o formato e como ele impacta o bolão.',
      readTimeMinutes: 8,
      coverImageAlt: 'Capa: Libertadores 2026 — guia completo',
      keywords: ['libertadores 2026', 'conmebol', 'fase de grupos', 'oitavas libertadores', 'bolão libertadores'],
      content: (
        <>
          <p>
            A Copa Libertadores da América é o torneio de clubes mais importante
            do continente. Existe desde 1960, tem uma tradição enorme e —
            diferente da maioria das competições continentais — combina fase de
            grupos longa com mata-mata em ida e volta até as semifinais, e final
            em jogo único. Para o bolão, esse mix de formatos cria desafios
            especiais.
          </p>
          <p>
            Neste guia, vamos passar pelo formato atual, pelas particularidades
            do chaveamento e pelas estratégias que funcionam na hora de palpitar.
          </p>

          <h2>O formato atual da Libertadores</h2>
          <p>
            A Libertadores hoje tem essencialmente quatro etapas:
          </p>
          <ol>
            <li>
              <strong>Fases preliminares (1ª, 2ª, 3ª fase):</strong> times de
              ranking menor disputam vagas pra fase de grupos. Costuma haver
              surpresas — clubes médios eliminando favoritos.
            </li>
            <li>
              <strong>Fase de grupos:</strong> 32 times divididos em 8 grupos de
              4. Cada um joga contra os outros do grupo, ida e volta (6 partidas
              por equipe). Avançam os 2 primeiros de cada grupo.
            </li>
            <li>
              <strong>Mata-mata (oitavas, quartas, semis):</strong> ida e volta.
              Os terceiros colocados da fase de grupos vão pra Sul-Americana —
              uma espécie de "Liga Europa" sul-americana.
            </li>
            <li>
              <strong>Final:</strong> jogo único, em estádio neutro escolhido
              pela CONMEBOL.
            </li>
          </ol>

          <h2>O detalhe do chaveamento (que muita gente ignora)</h2>
          <p>
            Aqui mora um dos pontos mais interessantes para quem joga bolão. Ao
            contrário do que parece, o chaveamento da Libertadores a partir das
            oitavas <strong>não é totalmente aleatório</strong>. A CONMEBOL faz
            um sorteio direcionado: o time melhor classificado da fase de
            grupos enfrenta o pior, segundo melhor enfrenta o segundo pior, e
            assim por diante.
          </p>
          <p>
            Mais que isso: <strong>os confrontos do mata-mata seguem um bracket
            pré-determinado</strong> que evita que dois times do mesmo grupo se
            enfrentem nas oitavas. O bracket clássico que a CONMEBOL usa há
            anos é o seguinte:
          </p>
          <ul>
            <li>Oitavas: A x H, B x G, C x F, D x E (onde A é o 1º colocado geral, H é o 8º)</li>
            <li>Quartas: (A/H) x (D/E), (B/G) x (C/F)</li>
            <li>Semis: (vencedor das quartas)</li>
          </ul>
          <p>
            No Bolão Elite, esse chaveamento já está incorporado no{' '}
            <a href="/simulador-libertadores-2026">simulador da Libertadores
            2026</a> — você simula resultado por resultado e o sistema monta o
            bracket automaticamente, respeitando o critério oficial CONMEBOL.
            É a forma mais eficiente de treinar suas previsões antes do mata-mata.
          </p>

          <h2>Por que a Libertadores é especial pro bolão</h2>

          <h3>1. Mistura de favoritos e desconhecidos</h3>
          <p>
            Na Libertadores, você tem clubes que conhece muito bem (Flamengo,
            Palmeiras, River Plate, Boca Juniors) e clubes que provavelmente
            nunca acompanhou (Universitário, Olimpia, LDU, Always Ready, etc.).
            Isso cria <strong>uma assimetria de informação que abre oportunidade
            de zebra</strong>: o time peruano pode jogar muito bem em altitude
            ou em casa, mas você só percebe se acompanha o campeonato peruano.
          </p>

          <h3>2. A altitude muda tudo</h3>
          <p>
            Estádios em La Paz (3.640m), Quito (2.850m) e Cusco (3.400m) são
            infernos para visitantes. <strong>Equipes brasileiras costumam
            sofrer goleadas em altitude</strong> mesmo sendo favoritos no
            papel. Antes de palpitar contra um time boliviano, peruano ou
            equatoriano fora de casa, leve a altitude em consideração.
          </p>

          <h3>3. Conmebol = imprevisibilidade</h3>
          <p>
            Comparado à Champions League europeia, onde os 8 maiores clubes
            do continente costumam dominar, a Libertadores é muito mais
            equilibrada. <strong>De 2014 pra cá, tivemos campeões diferentes
            de sete países distintos</strong>. A democracia continental é
            gigante — o que é ótimo pro torcedor e desafiador pro
            palpiteiro.
          </p>

          <h2>Estratégia: como palpitar na Libertadores</h2>

          <h3>Fase de grupos: cuide do empate</h3>
          <p>
            Na fase de grupos, jogos entre equipes de força similar terminam
            empatados com mais frequência que em campeonatos nacionais.
            <strong> Aproximadamente 30% das partidas da fase de grupos
            terminam empatadas</strong>. No bolão, palpitar empate é menos
            glamouroso mas estatisticamente vale a pena em alguns jogos
            equilibrados.
          </p>

          <h3>Mata-mata: jogo de volta decide</h3>
          <p>
            Como na Copa do Brasil, o jogo de volta tem mais peso. Equipes
            que perdem o primeiro jogo costumam abrir o jogo na volta —
            atacam mais, deixam espaço, criam jogos mais movimentados. <strong>Placares
            2x1 e 2x0 são comuns em jogos de volta</strong>.
          </p>

          <h3>Final em jogo único: cabeça quente</h3>
          <p>
            A final da Libertadores em jogo único, em estádio neutro, é
            única e imprevisível. Sem time da casa, sem retorno se errar,
            as decisões são quase sempre apertadas. <strong>De 2019 pra cá,
            2 das 5 finais foram decididas no tempo regulamentar
            comuns</strong> — as outras foram pra prorrogação ou pênaltis.
          </p>

          <h2>Como montar seu bolão da Libertadores</h2>
          <p>
            No Bolão Elite, a <a href="/bolao-libertadores">página oficial do
            bolão da Libertadores</a> permite criar bolão com poucas
            configurações: nome, privacidade e sistema de pontuação. As
            partidas são importadas automaticamente da API oficial — você
            não precisa adicionar jogo a jogo.
          </p>
          <p>
            Recomendação de pontuação: Regra C (10/5/2) — placar exato 10
            pts, vencedor/empate 5 pts, gols de um dos times 2 pts. Em
            Libertadores muito jogo é decidido por 1 gol — a Regra C aproveita
            bem o acerto parcial. Quem prefere simplicidade pode usar a Regra
            B (3/1).
          </p>

          <h2>Conclusão</h2>
          <p>
            A Libertadores recompensa quem acompanha futebol sul-americano,
            respeita altitude e entende o bracket fixo do mata-mata. Para
            bolão, é um torneio que exige mais leitura que palpite no
            chute — quem dedica 10 minutos por semana ao acompanhamento
            do torneio costuma ter resultado bem melhor do que quem palpita
            no automático.
          </p>
          <p>
            Para mais conteúdo, veja{' '}
            <a href="/blog">todos os artigos do blog</a> ou o{' '}
            <a href="/glossario">glossário</a>.
          </p>
        </>
      ),
    },
    en: {
      slug: 'copa-libertadores-2026',
      title: 'Copa Libertadores 2026: group stage, round of 16 and the secret of the cross-bracket ties',
      excerpt:
        'The most important tournament in South America has 32 teams in the group stage, an 8-step knockout and a single-match final. Understand the format and how it impacts the pool.',
      readTimeMinutes: 8,
      coverImageAlt: 'Cover: Copa Libertadores 2026 — complete guide',
      coverImage: '/blog/libertadores-2026-en.svg',
      keywords: ['copa libertadores 2026', 'conmebol', 'group stage', 'libertadores round of 16', 'copa libertadores pool'],
      content: (
        <>
          <p>
            The Copa Libertadores is the most important club tournament on the
            continent. It has existed since 1960, has a huge tradition and —
            unlike most continental competitions — combines a long group stage
            with a two-legged knockout up to the semi-finals, and a single-match
            final. For the pool, this mix of formats creates special challenges.
          </p>
          <p>
            In this guide, we will go through the current format, the quirks of
            the bracket and the strategies that work when it comes to making
            predictions.
          </p>

          <h2>The current Libertadores format</h2>
          <p>
            The Libertadores today has essentially four stages:
          </p>
          <ol>
            <li>
              <strong>Preliminary stages (1st, 2nd, 3rd stage):</strong>{' '}
              lower-ranked teams fight for berths in the group stage. There are
              usually surprises — mid-tier clubs knocking out favourites.
            </li>
            <li>
              <strong>Group stage:</strong> 32 teams split into 8 groups of 4.
              Each one plays the others in the group, two legs (6 matches per
              team). The top 2 of each group advance.
            </li>
            <li>
              <strong>Knockout stage (round of 16, quarter-finals,
              semi-finals):</strong> two legs. The third-placed teams from the
              group stage drop into the Copa Sudamericana — a sort of South
              American "Europa League".
            </li>
            <li>
              <strong>Final:</strong> single match, at a neutral stadium chosen
              by CONMEBOL.
            </li>
          </ol>

          <h2>The bracket detail (that many people overlook)</h2>
          <p>
            Here lies one of the most interesting points for pool players.
            Contrary to how it seems, the Libertadores bracket from the round of
            16 onwards <strong>is not entirely random</strong>. CONMEBOL runs a
            seeded draw: the best-ranked team from the group stage faces the
            worst, the second best faces the second worst, and so on.
          </p>
          <p>
            More than that: <strong>the knockout ties follow a pre-determined
            bracket</strong> that prevents two teams from the same group from
            meeting in the round of 16. The classic bracket CONMEBOL has used for
            years is the following:
          </p>
          <ul>
            <li>Round of 16: A v H, B v G, C v F, D v E (where A is the best overall, H is the 8th)</li>
            <li>Quarter-finals: (A/H) v (D/E), (B/G) v (C/F)</li>
            <li>Semi-finals: (the quarter-final winners)</li>
          </ul>
          <p>
            At Bolão Elite, this bracket is already built into the{' '}
            <a href="/en/copa-libertadores-2026-simulator">Copa Libertadores
            2026 simulator</a> — you simulate result by result and the system
            builds the bracket automatically, respecting the official CONMEBOL
            criterion. It is the most efficient way to train your predictions
            before the knockout stage.
          </p>

          <h2>Why the Libertadores is special for the pool</h2>

          <h3>1. A mix of favourites and unknowns</h3>
          <p>
            In the Libertadores, you have clubs you know very well (Flamengo,
            Palmeiras, River Plate, Boca Juniors) and clubs you have probably
            never followed (Universitario, Olimpia, LDU, Always Ready, etc.).
            This creates <strong>an information asymmetry that opens up a chance
            for an upset</strong>: the Peruvian team may play very well at
            altitude or at home, but you only notice if you follow the Peruvian
            league.
          </p>

          <h3>2. Altitude changes everything</h3>
          <p>
            Stadiums in La Paz (3,640m), Quito (2,850m) and Cusco (3,400m) are
            hell for away teams. <strong>Brazilian teams tend to suffer
            thrashings at altitude</strong> even when they are favourites on
            paper. Before predicting against a Bolivian, Peruvian or Ecuadorian
            team away from home, take altitude into account.
          </p>

          <h3>3. Conmebol = unpredictability</h3>
          <p>
            Compared to the European Champions League, where the 8 biggest clubs
            on the continent tend to dominate, the Libertadores is far more
            balanced. <strong>Since 2014, we have had different champions from
            seven distinct countries</strong>. The continental democracy is huge
            — which is great for the fan and challenging for the predictor.
          </p>

          <h2>Strategy: how to predict the Libertadores</h2>

          <h3>Group stage: mind the draw</h3>
          <p>
            In the group stage, matches between teams of similar strength end in
            a draw more often than in national championships.
            <strong> Roughly 30% of group-stage matches end in a draw</strong>.
            In the pool, predicting a draw is less glamorous but statistically
            worth it in some balanced matches.
          </p>

          <h3>Knockout stage: the second leg decides</h3>
          <p>
            As in the Copa do Brasil, the second leg carries more weight. Teams
            that lose the first match tend to open up in the return — they
            attack more, leave space, create more end-to-end matches.{' '}
            <strong>Scores of 2-1 and 2-0 are common in second legs</strong>.
          </p>

          <h3>Single-match final: hot heads</h3>
          <p>
            The single-match Libertadores final, at a neutral stadium, is unique
            and unpredictable. With no home team, no way back if you slip up, the
            deciders are almost always tight. <strong>Since 2019, 2 of the 5
            finals were decided in normal time</strong> — the others went to
            extra time or penalties.
          </p>

          <h2>How to set up your Libertadores pool</h2>
          <p>
            At Bolão Elite, the <a href="/en/copa-libertadores-pool">official
            Copa Libertadores pool page</a> lets you create a pool with just a
            few settings: name, privacy and scoring system. The matches are
            imported automatically from the official API — you don't need to add
            them match by match.
          </p>
          <p>
            Scoring recommendation: Rule C (10/5/2) — exact score 10 pts,
            winner/draw 5 pts, goals for one of the teams 2 pts. In the
            Libertadores, many matches are decided by 1 goal — Rule C makes good
            use of the partial match. Those who prefer simplicity can use Rule B
            (3/1).
          </p>

          <h2>Conclusion</h2>
          <p>
            The Libertadores rewards those who follow South American football,
            respect altitude and understand the fixed knockout bracket. For the
            pool, it is a tournament that demands more reading than guesswork —
            whoever devotes 10 minutes a week to following the tournament tends
            to get a much better result than whoever predicts on autopilot.
          </p>
          <p>
            For more content, see{' '}
            <a href="/en/blog">all blog articles</a> or the{' '}
            <a href="/en/glossary">glossary</a>.
          </p>
        </>
      ),
    },
    es: {
      slug: 'libertadores-2026',
      title: 'Libertadores 2026: fase de grupos, octavos y el secreto de los cruces',
      excerpt:
        'El torneo más importante de Sudamérica tiene 32 equipos en la fase de grupos, eliminatoria en 8 etapas y final a partido único. Entiende el formato y cómo impacta la quiniela.',
      readTimeMinutes: 8,
      coverImageAlt: 'Portada: Libertadores 2026 — guía completa',
      coverImage: '/blog/libertadores-2026-es.svg',
      keywords: ['libertadores 2026', 'conmebol', 'fase de grupos', 'octavos libertadores', 'quiniela libertadores'],
      content: (
        <>
          <p>
            La Copa Libertadores de América es el torneo de clubes más importante
            del continente. Existe desde 1960, tiene una tradición enorme y — a
            diferencia de la mayoría de las competiciones continentales —
            combina una fase de grupos larga con una eliminatoria a ida y vuelta
            hasta las semifinales, y una final a partido único. Para la quiniela,
            esa mezcla de formatos crea desafíos especiales.
          </p>
          <p>
            En esta guía vamos a repasar el formato actual, las particularidades
            del cuadro y las estrategias que funcionan a la hora de pronosticar.
          </p>

          <h2>El formato actual de la Libertadores</h2>
          <p>
            La Libertadores hoy tiene esencialmente cuatro etapas:
          </p>
          <ol>
            <li>
              <strong>Fases preliminares (1.ª, 2.ª, 3.ª fase):</strong> los
              equipos de menor ranking disputan cupos para la fase de grupos.
              Suele haber sorpresas — clubes medianos eliminando a favoritos.
            </li>
            <li>
              <strong>Fase de grupos:</strong> 32 equipos divididos en 8 grupos
              de 4. Cada uno juega contra los demás del grupo, ida y vuelta (6
              partidos por equipo). Avanzan los 2 primeros de cada grupo.
            </li>
            <li>
              <strong>Eliminatoria (octavos, cuartos, semifinales):</strong> ida
              y vuelta. Los terceros de la fase de grupos pasan a la Copa
              Sudamericana — una especie de "Europa League" sudamericana.
            </li>
            <li>
              <strong>Final:</strong> partido único, en un estadio neutral
              elegido por la CONMEBOL.
            </li>
          </ol>

          <h2>El detalle del cuadro (que mucha gente ignora)</h2>
          <p>
            Aquí está uno de los puntos más interesantes para quien juega la
            quiniela. Al contrario de lo que parece, el cuadro de la Libertadores
            a partir de los octavos <strong>no es totalmente aleatorio</strong>.
            La CONMEBOL hace un sorteo dirigido: el equipo mejor clasificado de la
            fase de grupos enfrenta al peor, el segundo mejor enfrenta al segundo
            peor, y así sucesivamente.
          </p>
          <p>
            Más que eso: <strong>los cruces de la eliminatoria siguen un cuadro
            predeterminado</strong> que evita que dos equipos del mismo grupo se
            enfrenten en octavos. El cuadro clásico que la CONMEBOL usa desde hace
            años es el siguiente:
          </p>
          <ul>
            <li>Octavos: A vs H, B vs G, C vs F, D vs E (donde A es el 1.º general, H es el 8.º)</li>
            <li>Cuartos: (A/H) vs (D/E), (B/G) vs (C/F)</li>
            <li>Semifinales: (los ganadores de cuartos)</li>
          </ul>
          <p>
            En Bolão Elite, ese cuadro ya está incorporado en el{' '}
            <a href="/es/simulador-libertadores-2026">simulador de la
            Libertadores 2026</a> — simulas resultado por resultado y el sistema
            arma el cuadro automáticamente, respetando el criterio oficial de la
            CONMEBOL. Es la forma más eficiente de entrenar tus pronósticos antes
            de la eliminatoria.
          </p>

          <h2>Por qué la Libertadores es especial para la quiniela</h2>

          <h3>1. Mezcla de favoritos y desconocidos</h3>
          <p>
            En la Libertadores tienes clubes que conoces muy bien (Flamengo,
            Palmeiras, River Plate, Boca Juniors) y clubes que probablemente
            nunca seguiste (Universitario, Olimpia, LDU, Always Ready, etc.).
            Esto crea <strong>una asimetría de información que abre la
            oportunidad de la sorpresa</strong>: el equipo peruano puede jugar muy
            bien en altura o en casa, pero solo lo notas si sigues el campeonato
            peruano.
          </p>

          <h3>2. La altura lo cambia todo</h3>
          <p>
            Los estadios en La Paz (3.640 m), Quito (2.850 m) y Cusco (3.400 m)
            son un infierno para los visitantes. <strong>Los equipos brasileños
            suelen sufrir goleadas en la altura</strong> incluso siendo favoritos
            sobre el papel. Antes de pronosticar contra un equipo boliviano,
            peruano o ecuatoriano fuera de casa, ten en cuenta la altura.
          </p>

          <h3>3. Conmebol = imprevisibilidad</h3>
          <p>
            Comparada con la Champions League europea, donde los 8 mayores clubes
            del continente suelen dominar, la Libertadores es mucho más
            equilibrada. <strong>De 2014 hacia acá, hemos tenido campeones
            diferentes de siete países distintos</strong>. La democracia
            continental es enorme — lo que es genial para el aficionado y
            desafiante para el pronosticador.
          </p>

          <h2>Estrategia: cómo pronosticar la Libertadores</h2>

          <h3>Fase de grupos: cuida el empate</h3>
          <p>
            En la fase de grupos, los partidos entre equipos de fuerza similar
            terminan en empate con más frecuencia que en los campeonatos
            nacionales.
            <strong> Aproximadamente el 30% de los partidos de la fase de grupos
            terminan empatados</strong>. En la quiniela, pronosticar el empate es
            menos glamuroso pero estadísticamente vale la pena en algunos
            partidos equilibrados.
          </p>

          <h3>Eliminatoria: el partido de vuelta decide</h3>
          <p>
            Como en la Copa de Brasil, el partido de vuelta tiene más peso. Los
            equipos que pierden el primer partido suelen abrir el juego en la
            vuelta — atacan más, dejan espacio, generan partidos más movidos.{' '}
            <strong>Los marcadores 2-1 y 2-0 son comunes en los partidos de
            vuelta</strong>.
          </p>

          <h3>Final a partido único: cabeza caliente</h3>
          <p>
            La final de la Libertadores a partido único, en un estadio neutral,
            es única e imprevisible. Sin equipo local, sin vuelta atrás si fallas,
            las definiciones son casi siempre ajustadas. <strong>De 2019 hacia
            acá, 2 de las 5 finales se decidieron en el tiempo
            reglamentario</strong> — las otras fueron a la prórroga o a los
            penales.
          </p>

          <h2>Cómo armar tu quiniela de la Libertadores</h2>
          <p>
            En Bolão Elite, la <a href="/es/quiniela-libertadores">página oficial
            de la quiniela de la Libertadores</a> permite crear una quiniela con
            pocas configuraciones: nombre, privacidad y sistema de puntuación. Los
            partidos se importan automáticamente de la API oficial — no necesitas
            añadirlos partido a partido.
          </p>
          <p>
            Recomendación de puntuación: Regla C (10/5/2) — marcador exacto 10
            pts, ganador/empate 5 pts, goles de uno de los equipos 2 pts. En la
            Libertadores muchos partidos se deciden por 1 gol — la Regla C
            aprovecha bien el acierto parcial. Quien prefiere la simplicidad puede
            usar la Regla B (3/1).
          </p>

          <h2>Conclusión</h2>
          <p>
            La Libertadores recompensa a quien sigue el fútbol sudamericano,
            respeta la altura y entiende el cuadro fijo de la eliminatoria. Para
            la quiniela, es un torneo que exige más lectura que pronóstico al
            azar — quien dedica 10 minutos por semana al seguimiento del torneo
            suele tener un resultado bastante mejor que quien pronostica en
            automático.
          </p>
          <p>
            Para más contenido, mira{' '}
            <a href="/es/blog">todos los artículos del blog</a> o el{' '}
            <a href="/es/glosario">glosario</a>.
          </p>
        </>
      ),
    },
  },
};
